// src/domains/core/dashboard/screen/DashboardScreen.tsx

import { type FC, useMemo } from "react";

import { Container, Heading, Stack } from "@/components/ui";
import { useContextIntelligence } from "@/domains/core/context-intelligence/hooks/useContextIntelligence";
import { debugHealth } from "@/domains/logging/utils/debugLog";
import { cn } from "@/shared/utils";

import { DashboardSettings } from "../components/controls/DashboardSettings";
import { getWidgetColSpan } from "../components/layout";
import { DashboardSection } from "../components/layout/DashboardSection";
import { QuickAccessBar } from "../components/layout/QuickAccessBar";
// Widget Imports
import {
  RoadmapMetricsWidget,
  CodeQualityWidget,
  CodeQualityMatrixWidget,
  RoadmapTopTasksWidget,
  GitStatusWidget,
} from "../components/widgets/admin";
import {
  OuraKeyMetricsWidget,
  OuraStepsWidget,
  OuraCorrectionFactorWidget,
  WithingsSummaryWidget,
  YazioNutritionWidget,
} from "../components/widgets/health";
import { ContextInsightsWidget } from "../components/widgets/insights";
import { ParqetSummaryWidget } from "../components/widgets/investments";
import { LLMServicesWidget } from "../components/widgets/llm";
import {
  TimeTracQTaskWidget,
  TimeTracQDailyPlanWidget,
  MiracleFlowStatusWidget,
  RemindersWidget,
} from "../components/widgets/productivity";
import { SmartHomeStatusWidget } from "../components/widgets/smarthome";
import { useDashboardWidgets } from "../hooks/useDashboardWidgets";
import { WIDGET_IDS } from "../store/widgetRegistry";

/**
 * Widget Component Map
 * Maps widget IDs to their React components
 */
const WIDGET_COMPONENT_MAP: Record<string, FC> = {
  [WIDGET_IDS.CODE_QUALITY]: CodeQualityWidget,
  [WIDGET_IDS.CODE_QUALITY_MATRIX]: CodeQualityMatrixWidget,
  // Insights
  [WIDGET_IDS.CONTEXT_INSIGHTS]: ContextInsightsWidget,
  [WIDGET_IDS.GIT_STATUS]: GitStatusWidget,
  // LLM
  [WIDGET_IDS.LLM_SERVICES]: LLMServicesWidget,

  [WIDGET_IDS.MIRACLEFLOW_STATUS]: MiracleFlowStatusWidget,
  [WIDGET_IDS.OURA_CORRECTION_FACTOR]: OuraCorrectionFactorWidget,
  // Health
  [WIDGET_IDS.OURA_KEY_METRICS]: OuraKeyMetricsWidget,
  [WIDGET_IDS.OURA_STEPS]: OuraStepsWidget,

  // Investments
  [WIDGET_IDS.PARQET_SUMMARY]: ParqetSummaryWidget,
  [WIDGET_IDS.REMINDERS]: RemindersWidget,
  // Admin
  [WIDGET_IDS.ROADMAP_METRICS]: RoadmapMetricsWidget,
  [WIDGET_IDS.ROADMAP_TOP_TASKS]: RoadmapTopTasksWidget,
  // Smart Home
  [WIDGET_IDS.SMARTHOME_STATUS]: SmartHomeStatusWidget,

  [WIDGET_IDS.TIMETRACQ_DAILY_PLAN]: TimeTracQDailyPlanWidget,

  // Productivity
  [WIDGET_IDS.TIMETRACQ_TASKS]: TimeTracQTaskWidget,

  [WIDGET_IDS.WITHINGS_SUMMARY]: WithingsSummaryWidget,

  [WIDGET_IDS.YAZIO_NUTRITION]: YazioNutritionWidget,
};

/**
 * DashboardScreen - Komplett neu gestaltetes Dashboard mit Widget-System
 *
 * Integriert alle Domains/Module in einem modularen, anpassbaren Layout
 *
 * @returns {JSX.Element} Dashboard screen component
 *
 */
export const DashboardScreen: FC = () => {
  const { getWidgetsByCategory, visibleWidgets } = useDashboardWidgets();

  // Initialize Context Intelligence Layer
  useContextIntelligence();

  // Group widgets by category (already filtered to visible widgets)
  const healthWidgets = getWidgetsByCategory("health");
  const productivityWidgets = getWidgetsByCategory("productivity");
  const adminWidgets = getWidgetsByCategory("admin");
  const investmentWidgets = getWidgetsByCategory("investments");
  const llmWidgets = getWidgetsByCategory("llm");
  const smarthomeWidgets = getWidgetsByCategory("smarthome");
  const insightsWidgets = getWidgetsByCategory("insights");

  // Hero Section: Most important widgets (only Tasks, not Oura - Oura is in Health section)
  const heroWidgets = useMemo(() => {
    return visibleWidgets.filter((w) => w.id === WIDGET_IDS.TIMETRACQ_TASKS);
  }, [visibleWidgets]);

  // Render Widget Component
  const renderWidget = (widgetId: string) => {
    const WidgetComponent = WIDGET_COMPONENT_MAP[widgetId];
    if (!WidgetComponent) {
      debugHealth("warn", "dashboard", "Widget component not found for ID", {
        file: "DashboardScreen.tsx",
        function: "renderWidget",
        widgetId,
      });
      return null;
    }
    const widget = visibleWidgets.find((w) => w.id === widgetId);
    const widgetSize = widget?.size ?? "small";
    return (
      <div className={cn(getWidgetColSpan(widgetSize), "w-full")} key={widgetId}>
        <WidgetComponent />
      </div>
    );
  };

  return (
    <Container className="text-slate-100 w-full" maxWidth="full" padding="lg">
      <Stack className="w-full" direction="col" gap={6}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <Heading level={1}>Dashboard</Heading>
          <DashboardSettings />
        </div>

        {/* Quick Access Bar */}
        <QuickAccessBar />

        {/* Hero Section - Most Important Metrics */}
        {heroWidgets.length > 0 && (
          <DashboardSection
            collapsible={false}
            description="Your most important metrics at a glance"
            id="hero"
            title="Quick Overview"
          >
            {heroWidgets.map((widget) => renderWidget(widget.id))}
          </DashboardSection>
        )}

        {/* Health & Productivity Sections - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
          {/* Health Section */}
          {healthWidgets.length > 0 && (
            <DashboardSection
              collapsible
              defaultCollapsed={false}
              description="Health metrics and tracking"
              id="health"
              title="Health"
            >
              {healthWidgets.map((widget) => renderWidget(widget.id))}
            </DashboardSection>
          )}

          {/* Productivity Section */}
          {productivityWidgets.length > 0 && (
            <DashboardSection
              collapsible
              defaultCollapsed={false}
              description="Tasks, time tracking, and personal development"
              id="productivity"
              title="Productivity"
            >
              {productivityWidgets.map((widget) => renderWidget(widget.id))}
            </DashboardSection>
          )}
        </div>

        {/* Contextual Insights Section */}
        {insightsWidgets.length > 0 && (
          <DashboardSection
            collapsible
            defaultCollapsed={false}
            description="Intelligent interpretations and recommendations"
            id="insights"
            title="Contextual Insights"
          >
            {insightsWidgets.map((widget) => renderWidget(widget.id))}
          </DashboardSection>
        )}

        {/* Admin & Analytics Section */}
        {adminWidgets.length > 0 && (
          <DashboardSection
            collapsible
            defaultCollapsed
            description="Project management and code analysis"
            id="admin"
            title="Admin & Analytics"
          >
            {adminWidgets.map((widget) => renderWidget(widget.id))}
          </DashboardSection>
        )}

        {/* Investments & LLM Sections - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
          {/* Investments Section */}
          {investmentWidgets.length > 0 && (
            <DashboardSection
              collapsible
              defaultCollapsed
              description="Portfolio and financial tracking"
              id="investments"
              title="Investments"
            >
              {investmentWidgets.map((widget) => renderWidget(widget.id))}
            </DashboardSection>
          )}

          {/* LLM Section */}
          {llmWidgets.length > 0 && (
            <DashboardSection
              collapsible
              defaultCollapsed
              description="LLM and AI tools"
              id="llm"
              title="AI Services"
            >
              {llmWidgets.map((widget) => renderWidget(widget.id))}
            </DashboardSection>
          )}
        </div>

        {/* Smart Home Section */}
        {smarthomeWidgets.length > 0 && (
          <DashboardSection
            collapsible
            defaultCollapsed
            description="Device status and controls"
            id="smarthome"
            title="Smart Home"
          >
            {smarthomeWidgets.map((widget) => renderWidget(widget.id))}
          </DashboardSection>
        )}
      </Stack>
    </Container>
  );
};
